import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import React__default from 'react';
import { useForm, FormProvider, useFormContext, useWatch, useFormState } from 'react-hook-form';
import { Button } from '../../atoms/Button.js';
import { Stack } from '../../layouts/Stack.js';
import { toast } from '../Toast.js';
import { FormTitle } from './FormTitle.js';
import './Form.scss';

/**
 * Extracts only the dirty values from the form data based on dirtyFields.
 * Handles nested objects recursively.
 */
function getDirtyValues(data, dirtyFields) {
    const dirtyValues = {};
    for (const key in dirtyFields) {
        if (dirtyFields[key] === true) {
            // Field is dirty, include its value
            dirtyValues[key] = data[key];
        }
        else if (typeof dirtyFields[key] === 'object' && dirtyFields[key] !== null) {
            // Nested object, recurse
            const nestedDirty = getDirtyValues(data[key], dirtyFields[key]);
            if (Object.keys(nestedDirty).length > 0) {
                dirtyValues[key] = nestedDirty;
            }
        }
    }
    return dirtyValues;
}
/**
 * Internal component that renders form content and watches toggle value.
 * Must be inside FormProvider to use useWatch hook.
 *
 * When the toggle is off, children are shown in a disabled state
 * (reduced opacity, non-interactive) so users can see what's configurable.
 */
function FormContent({ toggle, children, showSaveAndClear, defaultValues, }) {
    const { control } = useFormContext();
    // Use useWatch hook to watch the toggle field value
    const toggleValue = useWatch({
        control: control,
        name: toggle?.fieldName,
        defaultValue: toggle ? false : true,
    });
    const isInactive = toggle?.visibility !== 'hidden' && !toggleValue;
    const hasInactiveContent = isInactive && !!toggle?.inactiveContent;
    return (React__default.createElement(React__default.Fragment, null,
        hasInactiveContent && (React__default.createElement(Stack, { className: 'form-content--rev form-content--inactive', itemsSpacing: 4 }, toggle.inactiveContent)),
        React__default.createElement(Stack, { className: classNames('form-content--rev', {
                'form-content--hidden': isInactive && hasInactiveContent,
                'form-content--disabled': isInactive && !hasInactiveContent,
            }), itemsSpacing: 4 }, children),
        showSaveAndClear && React__default.createElement(FormSaveActions, { defaultValues: defaultValues })));
}
/**
 * Bottom-aligned Save and Clear buttons, shown only when the form is dirty.
 */
function FormSaveActions({ defaultValues, }) {
    const methods = useFormContext();
    const { isDirty, isSubmitting } = useFormState({ control: methods.control });
    if (!isDirty)
        return null;
    const handleClear = () => {
        methods.reset(defaultValues);
    };
    return (React__default.createElement("div", { className: 'form-save-actions--rev' },
        React__default.createElement(Button, { variant: 'outline', size: 'small', onPress: handleClear }, "Clear"),
        React__default.createElement(Button, { variant: 'filled', size: 'small', type: 'submit', isLoading: isSubmitting }, "Save")));
}
function Form({ schema, defaultValues, onSubmit, children, className, name, title, description, titleLevel = 'subsection', titleWeight = 'bold', formOptions, toggle, tag, titleMaxWidth, primaryButton, secondaryButton, showSaveAndClear = false, submitDirty = false, listenForServerChanges = true, forceValidation = false, }) {
    const methods = useForm({
        resolver: zodResolver(schema),
        defaultValues: defaultValues,
        values: listenForServerChanges
            ? defaultValues
            : undefined,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        ...formOptions,
    });
    React__default.useEffect(() => {
        if (forceValidation) {
            methods.trigger();
        }
    }, [forceValidation, methods]);
    const handleSubmit = methods.handleSubmit(async (data) => {
        // On successful validation
        // If submitDirty is true, only pass dirty fields to onSubmit
        const dataToSubmit = submitDirty
            ? getDirtyValues(data, methods.formState.dirtyFields)
            : data;
        const result = await onSubmit(dataToSubmit);
        // Reset form to default values only if submission was successful
        if (result.isSuccess) {
            methods.reset(defaultValues);
            toast({ message: 'Changes saved', variant: 'success', timeout: 2500 });
        }
        else {
            toast({ message: 'Something went wrong', variant: 'error', timeout: 2500 });
        }
    });
    return (React__default.createElement(FormProvider, { ...methods },
        React__default.createElement("form", { name: name, "data-rev-component": 'form-container', className: className, onSubmit: handleSubmit, noValidate: true },
            React__default.createElement(Stack, { itemsSpacing: 16 },
                title && (React__default.createElement(FormTitle, { title: title, description: description, level: titleLevel, tag: tag, maxWidth: titleMaxWidth, primaryButton: primaryButton, secondaryButton: secondaryButton, toggle: toggle, titleWeight: titleWeight })),
                React__default.createElement(FormContent, { toggle: toggle, showSaveAndClear: showSaveAndClear, defaultValues: defaultValues }, children)))));
}
Form.displayName = 'Form';

export { Form };
//# sourceMappingURL=Form.js.map
